import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import adminAxios from '../../services/adminAxios';
import '../../assets/css/admin/event-details.css';

const EventDetailsPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [checkinsData, setCheckinsData] = useState({
    checkins: [],
    loading: false
  });
  const [momentsData, setMomentsData] = useState({
    moments: [],
    loading: false
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const [selectedMoment, setSelectedMoment] = useState(null);

  const [csvFile, setCsvFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const csvFileInputRef = useRef(null);

  useEffect(() => {
    fetchEventDetails();
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      // Use the public event details endpoint, not the admin one
      const response = await adminAxios.get(`/api/events/${eventId}`);

      if (response.data && (response.data.success !== false)) {
        // If response.data is the event object itself, not wrapped in {event: ...}
        setEvent(response.data.event || response.data);
        setError(null);
      } else {
        setError('Failed to load event details');
      }
    } catch (err) {
      console.error('Error fetching event details:', err);
      setError('Error loading event details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    if (activeTab === 'checkins') {
      fetchCheckins();
    } else if (activeTab === 'moments') {
      fetchMoments();
    }
    
    const tabContent = document.querySelector('.tab-content');
    if (tabContent) {
      tabContent.style.opacity = '0.99';
      setTimeout(() => {
        tabContent.style.opacity = '1';
      }, 10);
    }
  }, [activeTab, eventId]);

  const fetchCheckins = async () => {
    try {
      setCheckinsData(prev => ({ ...prev, loading: true }));
      const response = await adminAxios.get(`/api/admin/events/${eventId}/checkins`);

      if (response.data.success) {
        const checkins = response.data.checkins || response.data.data || [];
        setCheckinsData({
          checkins: checkins,
          loading: false
        });
      } else {
        setCheckinsData({
          checkins: [],
          loading: false
        });
      }
    } catch (err) {
      console.error('Error fetching checkins:', err);
      setCheckinsData(prev => ({
        ...prev,
        loading: false,
        checkins: []
      }));
    }
  };

  const fetchMoments = async () => {
    try {
      setMomentsData(prev => ({ ...prev, loading: true }));

      const response = await adminAxios.get(`/api/admin/events/${eventId}/footages?include_details=true`);

      if (response.data.success) {
        setMomentsData({
          moments: response.data.footages || [],
          loading: false
        });
      } else {
        setMomentsData({
          moments: [],
          loading: false
        });
      }
    } catch (err) {
      console.error('Error fetching moments:', err);
      setMomentsData(prev => ({
        ...prev,
        loading: false,
        moments: []
      }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  const handleTabClick = (tab) => {
    if (tab === activeTab) return;
    
    setActiveTab(tab);
    if (tab === 'checkins') {
      setCheckinsData(prev => ({ ...prev, loading: true }));
    } else if (tab === 'moments') {
      setMomentsData(prev => ({ ...prev, loading: true }));
    }
    
    setTimeout(() => {
      const tabContent = document.querySelector('.tab-content');
      if (tabContent) {
        tabContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleEditMode = () => {
    if (!isEditMode) {
      setEditFormData({
        event_title: event?.event_title || '',
        event_description: event?.event_description || '',
        event_date: event?.event_date || '',
        event_time: event?.event_time || '',
        event_end_time: event?.event_end_time || '',
        location: event?.location || '',
        event_summary: event?.event_summary || '',
        status: event?.status || 'active',
      });
      setImagePreview(event?.event_image || null);
    }
    setIsEditMode(!isEditMode);
    setFormError(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditFormData(prev => ({
        ...prev,
        event_image: file
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);

    try {
      const formData = new FormData();

      Object.keys(editFormData).forEach(key => {
        if (key !== 'event_image' || editFormData[key] !== null) {
          formData.append(key, editFormData[key]);
        }
      });

      const response = await adminAxios.put(`/api/admin/events/${eventId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        await fetchEventDetails();
        setIsEditMode(false);
      } else {
        setFormError(response.data.message || 'Failed to update event');
      }
    } catch (err) {
      console.error('Error updating event:', err);
      setFormError('Error updating event. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDeleteConfirm = () => {
    setShowDeleteConfirm(!showDeleteConfirm);
  };

  const handleDeleteEvent = async () => {
    setIsDeleting(true);

    try {
      const response = await adminAxios.delete(`/api/admin/events/${eventId}`);

      if (response.data.success) {
        navigate('/admin/events');
      } else {
        alert(response.data.message || 'Failed to delete event');
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Error deleting event. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleViewMoment = (moment) => {
    setSelectedMoment(moment);
  };

  const closeMomentView = () => {
    setSelectedMoment(null);
  };

  const handleCsvFileChange = (e) => {
    if (e.target.files[0]) {
      setCsvFile(e.target.files[0]);
    }
  };

  const resetCsvUpload = () => {
    setCsvFile(null);
    setUploadStatus(null);
    if (csvFileInputRef.current) {
      csvFileInputRef.current.value = '';
    }
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();

    if (!csvFile) {
      setUploadStatus({
        type: 'error',
        message: 'Please select a CSV file to upload'
      });
      return;
    }

    setIsUploading(true);
    setUploadStatus(null);

    try {
      const formData = new FormData();
      formData.append('csv', csvFile);

      const response = await adminAxios.post(
        `/api/admin/events/${eventId}/process-moments-csv`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setUploadStatus({
          type: 'success',
          message: response.data.message,
          details: `${response.data.processedRows} rows processed, ${response.data.usersWithMoments} users with moments found.`
        });
        fetchMoments();
      } else {
        setUploadStatus({
          type: 'error',
          message: response.data.message || 'Failed to upload CSV'
        });
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setUploadStatus({
        type: 'error',
        message: 'Error uploading and processing CSV file',
        details: error.response?.data?.message || error.message
      });
    } finally {
      setIsUploading(false);
    }
  };

  const renderDetailsTab = () => (
    <div className="tab-pane">
      {event?.event_image ? (
        <div className="event-image-container">
          <img 
            src={event.event_image} 
            alt={event.event_title} 
            className="event-image" 
          />
        </div>
      ) : (
        <div className="event-image-container">
          <div className="event-image-placeholder">
            <div>
              <i className="fa fa-image"></i>
              <p>No image available for this event</p>
            </div>
          </div>
        </div>
      )}
      <div className="details-grid">
        <div className="details-main-card">
          <div className="card-title">
            <i className="fa fa-file-text-o"></i> Description
          </div>
          <div className="card-content">
            <p className="event-description">{event?.event_description || 'No description provided.'}</p>
          </div>
        </div>

        <div className="details-sidebar-card">
          <div className="card-title">
            <i className="fa fa-user"></i> Organizer
          </div>
          <div className="card-content">
            <div className="organizer-info">
              <div className="organizer-avatar">
                <i className="fa fa-user-circle"></i>
              </div>
              <div className="organizer-details">
                <div className="organizer-name">{event?.creator_name}</div>
                <div className="organizer-email">{event?.creator_email}</div>
                <Link to={`/admin/users/${event?.user_id}`} className="btn btn-sm btn-outline-primary mt-2">
                  View Profile
                </Link>
              </div>
            </div>
          </div>
        </div>

        {event?.event_summary && (
          <div className="details-summary-card">
            <div className="card-title">
              <i className="fa fa-list-alt"></i> Event Summary
            </div>
            <div className="card-content">
              <p>{event.event_summary}</p>
            </div>
          </div>
        )}

        <div className="details-dates-card">
          <div className="card-title">
            <i className="fa fa-calendar-check-o"></i> Event Timeline
          </div>
          <div className="card-content">
            <ul className="timeline">
              <li className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <h4>Created</h4>
                  <p>{formatDate(event?.created_at)} at {new Date(event?.created_at).toLocaleTimeString()}</p>
                </div>
              </li>
              <li className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <h4>Event Date</h4>
                  <p>{formatDate(event?.event_date)} at {event?.event_time}</p>
                </div>
              </li>
              {event?.updated_at && new Date(event.updated_at) > new Date(event.created_at) && (
                <li className="timeline-item">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <h4>Last Updated</h4>
                    <p>{formatDate(event?.updated_at)} at {new Date(event?.updated_at).toLocaleTimeString()}</p>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCheckinsTab = () => (
    <div className="tab-pane">
      <div className="tab-actions">
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={fetchCheckins}
          disabled={checkinsData.loading}
        >
          <i className="fa fa-refresh"></i> Refresh Check-ins
        </button>
      </div>
      {checkinsData.loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading check-ins...</p>
        </div>
      ) : checkinsData.checkins && checkinsData.checkins.length > 0 ? (
        <>
          {/* Show checked-in user names */}
          <div className="mb-3">
            <strong>Checked-in Users:</strong>
            <ul className="checkedin-users-list" style={{ margin: '10px 0 0 0', padding: 0, listStyle: 'none' }}>
              {checkinsData.checkins.map(checkin => (
                <li key={checkin.id} style={{ display: 'inline-block', marginRight: 12 }}>
                  <span className="badge bg-secondary" style={{ fontSize: '14px', padding: '6px 12px' }}>
                    {checkin.user_name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="checkins-grid">
            {checkinsData.checkins.map(checkin => (
              <div key={checkin.id} className="checkin-card">
                <div className="checkin-user">
                  <div className="checkin-avatar">
                    <i className="fa fa-user-circle"></i>
                  </div>
                  <div className="checkin-user-details">
                    <div className="checkin-username">{checkin.user_name}</div>
                    <div className="checkin-time">
                      <i className="fa fa-clock-o"></i> {new Date(checkin.checkin_date).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="checkin-footer">
                  <span className={`visibility-badge ${checkin.visibility || 'public'}`}>
                    <i className={`fa fa-${checkin.visibility === 'private' ? 'lock' : checkin.visibility === 'friends' ? 'users' : 'globe'}`}></i>
                    {checkin.visibility || 'Public'}
                  </span>
                  <Link to={`/admin/users/${checkin.user_id}`} className="btn btn-sm btn-outline-primary">
                    View User
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fa fa-check-square-o"></i>
          </div>
          <h3>No Check-ins Yet</h3>
          <p>This event doesn't have any check-ins recorded.</p>
        </div>
      )}
    </div>
  );

  const renderMomentsTab = () => (
    <div className="tab-pane">
      <div className="tab-actions d-flex justify-content-between">
        <div>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={fetchMoments}
            disabled={momentsData.loading}
          >
            <i className="fa fa-refresh"></i> Refresh Moments
          </button>
        </div>
        <div className="csv-upload-section">
          <form onSubmit={handleCsvUpload} className="d-flex align-items-center gap-2">
            <input
              type="file"
              className="form-control"
              accept=".csv"
              onChange={handleCsvFileChange}
              ref={csvFileInputRef}
              style={{ maxWidth: 250 }}
              required
            />
            <button
              type="submit"
              className="btn btn-success"
              disabled={!csvFile || isUploading}
            >
              {isUploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="fa fa-cloud-upload me-2"></i> Upload & Notify
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={resetCsvUpload}
              disabled={isUploading}
            >
              Cancel
            </button>
          </form>
        </div>
      </div>
      {uploadStatus && (
        <div className={`alert alert-${uploadStatus.type === 'success' ? 'success' : 'danger'} mt-3`}>
          <h6>{uploadStatus.message}</h6>
          {uploadStatus.details && <p className="mb-0 small">{uploadStatus.details}</p>}
        </div>
      )}
      {momentsData.loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading moments...</p>
        </div>
      ) : momentsData.moments && momentsData.moments.length > 0 ? (
        <div className="moments-table-container">
          <table className="table moments-table">
            <thead>
              <tr>
                <th>Preview</th>
                <th>Filename</th>
                <th>User</th>
                <th>Upload Date</th>
                <th>Type</th>
                <th>Size</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {momentsData.moments.map(moment => (
                <tr key={moment.id}>
                  <td className="moment-thumbnail-cell">
                    {moment.mime_type && moment.mime_type.startsWith('image/') ? (
                      <div className="moment-thumbnail">
                        <img
                          src={moment.thumbnail_url || moment.url}
                          alt="Thumbnail"
                          className="thumbnail-img"
                        />
                      </div>
                    ) : (
                      <div className="moment-thumbnail file-icon">
                        <i className="fa fa-file"></i>
                      </div>
                    )}
                  </td>
                  <td>{moment.footage_filename}</td>
                  <td>
                    {moment.user_name || 'Unknown'}
                    {moment.user_id && (
                      <Link
                        to={`/admin/users/${moment.user_id}`}
                        className="user-link"
                      >
                        <i className="fa fa-external-link-square"></i>
                      </Link>
                    )}
                  </td>
                  <td>{new Date(moment.uploaded_at).toLocaleString()}</td>
                  <td>{moment.mime_type || 'Unknown'}</td>
                  <td>{moment.file_size ? `${Math.round(moment.file_size / 1024)} KB` : 'N/A'}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => handleViewMoment(moment)}
                    >
                      <i className="fa fa-eye"></i> View
                    </button>
                    <a
                      href={moment.url}
                      download={moment.footage_filename}
                      className="btn btn-sm btn-outline-success"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fa fa-download"></i> Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fa fa-camera"></i>
          </div>
          <h3>No Moments Available</h3>
          <p>This event doesn't have any moments uploaded yet. Upload a CSV to process user moments.</p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading-container">
          <div className="spinner"></div>
          <p>Loading event details...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="alert alert-danger">{error}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="event-details-page">
        <div className="back-link">
          <Link to="/admin/events">
            <i className="fa fa-arrow-left"></i> Back to Events
          </Link>
        </div>

        <div className="event-details-header">
          <div className="event-header-content">
            <div className="event-status">
              <span className={`status-badge ${event?.status || 'active'}`}>
                {event?.status || 'Active'}
              </span>
            </div>

            <h1 className="event-title">{event?.event_title}</h1>

            <div className="event-meta-info">
              <div className="event-meta-item">
                <i className="fa fa-calendar"></i>
                <span>{formatDate(event?.event_date)}</span>
              </div>

              <div className="event-meta-item">
                <i className="fa fa-clock-o"></i>
                <span>{formatTime(event?.event_time)} - {formatTime(event?.event_end_time || '')}</span>
              </div>

              <div className="event-meta-item">
                <i className="fa fa-map-marker"></i>
                <span>{event?.location || 'No location specified'}</span>
              </div>
            </div>

            <div className="event-stats-bar">
              <div className="event-stat-item">
                <div className="stat-number">{event?.checkin_count || 0}</div>
                <div className="stat-label">Check-ins</div>
              </div>

              <div className="event-stat-item">
                <div className="stat-number">{event?.footage_count || 0}</div>
                <div className="stat-label">Moments</div>
              </div>
            </div>
          </div>

          <div className="event-header-actions">
            <button className="btn btn-outline-primary" onClick={toggleEditMode}>
              <i className="fa fa-pencil"></i> Edit Event
            </button>
            <button className="btn btn-outline-danger" onClick={toggleDeleteConfirm}>
              <i className="fa fa-trash"></i> Delete
            </button>
          </div>
        </div>

        {formError && (
          <div className="alert alert-danger mb-3">{formError}</div>
        )}

        {showDeleteConfirm && (
          <div className="modal-overlay">
            <div className="delete-confirm-modal bg_grey">
              <h4><i className="fa fa-exclamation-triangle text-danger"></i> Delete Event?</h4>
              <p className="text-white">Are you sure you want to delete "{event?.event_title}"? This action cannot be undone.</p>
              <p className="text-danger mb-4">All check-ins and moments associated with this event will also be deleted.</p>

              <div className="modal-actions">
                <button
                  className="btn btn-outline-secondary"
                  onClick={toggleDeleteConfirm}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteEvent}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    'Delete Event'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="modern-tabs">
          <div
            className={`tab-item ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => handleTabClick('details')}
            role="tab"
            aria-selected={activeTab === 'details'}
            tabIndex={0}
          >
            <i className="fa fa-info-circle"></i> Details
          </div>
          <div
            className={`tab-item ${activeTab === 'checkins' ? 'active' : ''}`}
            onClick={() => handleTabClick('checkins')}
            role="tab"
            aria-selected={activeTab === 'checkins'}
            tabIndex={0}
          >
            <i className="fa fa-check-square-o"></i> Check-ins <span className="tab-count">{event?.checkin_count || 0}</span>
          </div>
          <div
            className={`tab-item ${activeTab === 'moments' ? 'active' : ''}`}
            onClick={() => handleTabClick('moments')}
            role="tab"
            aria-selected={activeTab === 'moments'}
            tabIndex={0}
          >
            <i className="fa fa-camera"></i> Moments <span className="tab-count">{event?.footage_count || 0}</span>
          </div>
        </div>

        <div className="tab-content" role="tabpanel">
          {activeTab === 'details' && renderDetailsTab()}
          {activeTab === 'checkins' && renderCheckinsTab()}
          {activeTab === 'moments' && renderMomentsTab()}
        </div>

        {selectedMoment && (
          <div className="modal-overlay" onClick={closeMomentView}>
            <div className="moment-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h4>Moment Details</h4>
                <button type="button" className="close-btn" onClick={closeMomentView}>
                  <i className="fa fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <div className="moment-preview-container">
                  {selectedMoment.mime_type && selectedMoment.mime_type.startsWith('image/') ? (
                    <img 
                      src={selectedMoment.url} 
                      alt={selectedMoment.footage_filename} 
                      className="moment-preview-image" 
                    />
                  ) : selectedMoment.mime_type && selectedMoment.mime_type.startsWith('video/') ? (
                    <video 
                      controls 
                      className="moment-preview-video"
                    >
                      <source src={selectedMoment.url} type={selectedMoment.mime_type} />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <div className="file-preview-placeholder">
                      <i className="fa fa-file-o"></i>
                      <p>Preview not available</p>
                    </div>
                  )}
                </div>
                <div className="moment-details-info">
                  <h5>{selectedMoment.footage_filename}</h5>
                  
                  <table className="details-table">
                    <tbody>
                      <tr>
                        <td className="detail-label">Uploaded by:</td>
                        <td>{selectedMoment.user_name || 'Unknown'}</td>
                      </tr>
                      <tr>
                        <td className="detail-label">Upload Date:</td>
                        <td>{new Date(selectedMoment.uploaded_at).toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td className="detail-label">File Type:</td>
                        <td>{selectedMoment.mime_type || 'Unknown'}</td>
                      </tr>
                      <tr>
                        <td className="detail-label">File Size:</td>
                        <td>{selectedMoment.file_size ? `${Math.round(selectedMoment.file_size / 1024)} KB` : 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <a 
                  href={selectedMoment.url} 
                  download={selectedMoment.footage_filename}
                  className="btn btn-outline-success"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa fa-download"></i> Download
                </a>
              </div>
            </div>
          </div>
        )}

        {isEditMode && (
          <div className="modal-overlay">
            <div className="edit-event-modal bg_grey">
              <div className="modal-header border-0">
                <h4 className="text-white"><i className="fa fa-pencil text-primary me-2"></i> Edit Event</h4>
                <button type="button" className="close-btn" onClick={toggleEditMode}>
                  <i className="fa fa-times"></i>
                </button>
              </div>
              
              <div className="modal-body">
                <form id="editEventForm" onSubmit={handleSubmitUpdate}>
                  <div className="form-section">
                    <h5 className="text-white">Event Image</h5>
                    <div 
                      className="image-upload-container"
                      onClick={triggerFileInput}
                    >
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageChange}
                        accept="image/*"
                      />
                      
                      {imagePreview ? (
                        <div>
                          <img src={imagePreview} alt="Event preview" className="image-preview" />
                          <p className="mt-2">Click to change image</p>
                        </div>
                      ) : (
                        <div className="image-upload-placeholder">
                          <i className="fa fa-cloud-upload"></i>
                          <p>Click to upload event image</p>
                          <p className="small text-muted">Recommended size: 1200 x 600 pixels</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-section">
                    <h5 className="text-white">Basic Information</h5>
                    <div className="form-group mb-3">
                      <label htmlFor="event_title" className="form-label text-white">Event Title</label>
                      <input
                        type="text"
                        className="form-control bg-dark text-white"
                        id="event_title"
                        name="event_title"
                        value={editFormData.event_title || ''}
                        onChange={handleInputChange}
                        placeholder="Enter event title"
                        required
                      />
                    </div>
                    
                    <div className="form-group mb-3">
                      <label htmlFor="event_description" className="form-label text-white">Description</label>
                      <textarea
                        className="form-control bg-dark text-white"
                        id="event_description"
                        name="event_description"
                        rows="5"
                        value={editFormData.event_description || ''}
                        onChange={handleInputChange}
                        placeholder="Enter event description"
                      ></textarea>
                    </div>
                    
                    <div className="form-group mb-3">
                      <label htmlFor="event_summary" className="form-label text-white">Summary</label>
                      <textarea
                        className="form-control bg-dark text-white"
                        id="event_summary"
                        name="event_summary"
                        rows="3"
                        value={editFormData.event_summary || ''}
                        onChange={handleInputChange}
                        placeholder="Enter event summary"
                      ></textarea>
                    </div>
                  </div>
                  
                  <div className="form-section">
                    <h5 className="text-white">Date and Location</h5>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group mb-3">
                          <label htmlFor="event_date" className="form-label text-white">Event Date</label>
                          <input
                            type="date"
                            className="form-control bg-dark text-white"
                            id="event_date"
                            name="event_date"
                            value={editFormData.event_date || ''}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group mb-3">
                          <label htmlFor="event_time" className="form-label text-white">Start Time</label>
                          <input
                            type="time"
                            className="form-control bg-dark text-white"
                            id="event_time"
                            name="event_time"
                            value={editFormData.event_time || ''}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group mb-3">
                          <label htmlFor="event_end_time" className="form-label text-white">End Time</label>
                          <input
                            type="time"
                            className="form-control bg-dark text-white"
                            id="event_end_time"
                            name="event_end_time"
                            value={editFormData.event_end_time || ''}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-group mb-3">
                      <label htmlFor="location" className="form-label text-white">Location</label>
                      <input
                        type="text"
                        className="form-control bg-dark text-white"
                        id="location"
                        name="location"
                        value={editFormData.location || ''}
                        onChange={handleInputChange}
                        placeholder="Enter event location"
                      />
                    </div>
                  </div>
                  
                  <div className="form-section mb-0">
                    <h5 className="text-white">Event Status</h5>
                    <div className="form-group">
                      <label htmlFor="status" className="form-label text-white">Status</label>
                      <select
                        className="form-select bg-dark text-white"
                        id="status"
                        name="status"
                        value={editFormData.status || 'active'}
                        onChange={handleInputChange}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </form>
              </div>
              
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={toggleEditMode}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="editEventForm"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default EventDetailsPage;
